import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Button } from '@/components/ui/button'
import { Info } from 'lucide-react'

export interface RetrievalConfig {
  pagerank: number
  autoKeywords: number
  autoQuestions: number
}

interface Step3RetrievalEnhancementProps {
  config: RetrievalConfig
  onConfigChange: (config: RetrievalConfig) => void
}

const DEFAULT_RETRIEVAL_CONFIG: RetrievalConfig = {
  pagerank: 0,
  autoKeywords: 3,
  autoQuestions: 3,
}

export function Step3RetrievalEnhancement({ config, onConfigChange }: Step3RetrievalEnhancementProps) {
  const handleReset = () => {
    onConfigChange(DEFAULT_RETRIEVAL_CONFIG)
  }

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold mb-1">Retrieval Enhancement</h3>
        <p className="text-sm text-muted-foreground">
          These settings improve how well your knowledge base retrieves relevant information. They add metadata
          to each chunk during parsing, which helps the search engine find the right content.
        </p>
      </div>

      {/* Page Rank */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label>Page Rank</Label>
          <span className="text-sm font-medium">{config.pagerank}</span>
        </div>
        <Slider
          value={[config.pagerank]}
          onValueChange={([value]) => onConfigChange({ ...config, pagerank: value })}
          min={0}
          max={100}
          step={1}
        />
        <p className="text-xs text-muted-foreground flex items-start gap-1.5">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          Boosts the ranking of chunks from this knowledge base during retrieval. Higher values (e.g., 50–100)
          make chunks from this KB appear higher in search results. Useful when you have multiple knowledge bases
          and want to prioritize one over others. Leave at 0 if you have a single KB.
        </p>
      </div>

      {/* Auto Keywords */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label>Auto Keywords</Label>
          <span className="text-sm font-medium">{config.autoKeywords}</span>
        </div>
        <Slider
          value={[config.autoKeywords]}
          onValueChange={([value]) => onConfigChange({ ...config, autoKeywords: value })}
          min={0}
          max={32}
          step={1}
        />
        <p className="text-xs text-muted-foreground flex items-start gap-1.5">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          Automatically extracts keywords from each chunk using an LLM. These keywords are stored alongside the chunk
          and used during keyword-based (BM25) search to improve recall. Set to 0 to disable. A value of 3–5 works
          well for most use cases. Higher values increase parsing time and cost.
        </p>
      </div>

      {/* Auto Questions */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label>Auto Questions</Label>
          <span className="text-sm font-medium">{config.autoQuestions}</span>
        </div>
        <Slider
          value={[config.autoQuestions]}
          onValueChange={([value]) => onConfigChange({ ...config, autoQuestions: value })}
          min={0}
          max={10}
          step={1}
        />
        <p className="text-xs text-muted-foreground flex items-start gap-1.5">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          Automatically generates questions that each chunk can answer, using an LLM. These generated questions are
          indexed alongside the chunk, making it much easier to match user queries to the right content. Set to 0
          to disable. A value of 2–3 is recommended. Higher values increase parsing time and cost.
        </p>
      </div>

      {/* Reset Button */}
      <div className="pt-2">
        <Button type="button" variant="outline" onClick={handleReset}>
          Reset to Defaults
        </Button>
      </div>
    </div>
  )
}

export { DEFAULT_RETRIEVAL_CONFIG }
